import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  EstadoAdmin,
  LojistaAdmin,
  CriarLojistaPayload,
  EditarLojistaPayload,
  ResetSenhaPayload,
  VinculoLojista,
  EstabelecimentoAdmin,
} from '../tipos';
import { servicoAdmin } from '../servicos/servico.admin';

/**
 * Fatia administrativa do Redux — Gerenciamento de lojistas, vínculos e estabelecimentos
 * Requisitos: 2.3, 2.4, 3.5, 4.4, 5.1, 6.5, 7.4, 8.3, 9.3, 9.4
 *
 * - Estado de lojistas, estabelecimentos, vínculos e lojista em edição
 * - Thunks para CRUD de lojistas (listar, criar, editar, ativar, desativar, resetar senha)
 * - Thunks para vínculos (listar, criar, remover)
 * - Thunk para listar estabelecimentos
 * - Reducers para controle de estado local (edição, limpeza de erro/sucesso)
 */

// ============================================================
// Async Thunks — Lojistas
// ============================================================

/**
 * Lista todos os lojistas cadastrados.
 */
export const listarLojistas = createAsyncThunk<
  LojistaAdmin[],
  void,
  { rejectValue: string }
>('admin/listarLojistas', async (_, { rejectWithValue }) => {
  try {
    return await servicoAdmin.listarLojistas();
  } catch (erro: unknown) {
    const mensagem = erro instanceof Error ? erro.message : 'Erro ao listar lojistas';
    return rejectWithValue(mensagem);
  }
});

/**
 * Cria um novo lojista.
 */
export const criarLojista = createAsyncThunk<
  LojistaAdmin,
  CriarLojistaPayload,
  { rejectValue: string }
>('admin/criarLojista', async (payload, { rejectWithValue }) => {
  try {
    return await servicoAdmin.criarLojista(payload);
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as { response?: { data?: { message?: string; error?: string } } };
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }
      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro ao criar lojista');
  }
});

/**
 * Edita os dados de um lojista existente.
 */
export const editarLojista = createAsyncThunk<
  LojistaAdmin,
  { id: string; payload: EditarLojistaPayload },
  { rejectValue: string }
>('admin/editarLojista', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await servicoAdmin.editarLojista(id, payload);
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as { response?: { data?: { message?: string; error?: string } } };
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }
      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro ao editar lojista');
  }
});

/**
 * Ativa um lojista.
 */
export const ativarLojista = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('admin/ativarLojista', async (id, { rejectWithValue }) => {
  try {
    await servicoAdmin.ativarLojista(id);
    return id;
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as { response?: { data?: { message?: string; error?: string } } };
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro ao ativar lojista');
  }
});

/**
 * Desativa um lojista.
 */
export const desativarLojista = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('admin/desativarLojista', async (id, { rejectWithValue }) => {
  try {
    await servicoAdmin.desativarLojista(id);
    return id;
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as { response?: { data?: { message?: string; error?: string } } };
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro ao desativar lojista');
  }
});

/**
 * Reseta a senha de um lojista.
 */
export const resetarSenha = createAsyncThunk<
  string,
  { id: string; payload: ResetSenhaPayload },
  { rejectValue: string }
>('admin/resetarSenha', async ({ id, payload }, { rejectWithValue }) => {
  try {
    await servicoAdmin.resetarSenha(id, payload);
    return id;
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as { response?: { data?: { message?: string; error?: string } } };
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }
      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro ao resetar senha');
  }
});

// ============================================================
// Async Thunks — Vínculos
// ============================================================

/**
 * Lista os vínculos de um lojista.
 */
export const listarVinculos = createAsyncThunk<
  VinculoLojista[],
  string,
  { rejectValue: string }
>('admin/listarVinculos', async (idLojista, { rejectWithValue }) => {
  try {
    return await servicoAdmin.listarVinculos(idLojista);
  } catch (erro: unknown) {
    const mensagem = erro instanceof Error ? erro.message : 'Erro ao listar vínculos';
    return rejectWithValue(mensagem);
  }
});

/**
 * Cria um vínculo entre lojista e estabelecimento.
 */
export const criarVinculo = createAsyncThunk<
  { idLojista: string; idEstabelecimento: string },
  { idLojista: string; idEstabelecimento: string },
  { rejectValue: string }
>('admin/criarVinculo', async ({ idLojista, idEstabelecimento }, { rejectWithValue }) => {
  try {
    await servicoAdmin.criarVinculo(idLojista, idEstabelecimento);
    return { idLojista, idEstabelecimento };
  } catch (erro: unknown) {
    if (erro instanceof Error) {
      const axiosError = erro as { response?: { data?: { message?: string; error?: string } } };
      if (axiosError.response?.data?.message) {
        return rejectWithValue(axiosError.response.data.message);
      }
      if (axiosError.response?.data?.error) {
        return rejectWithValue(axiosError.response.data.error);
      }
      return rejectWithValue(erro.message);
    }
    return rejectWithValue('Erro ao criar vínculo');
  }
});

/**
 * Remove um vínculo entre lojista e estabelecimento.
 */
export const removerVinculo = createAsyncThunk<
  { idLojista: string; idEstabelecimento: string },
  { idLojista: string; idEstabelecimento: string },
  { rejectValue: string }
>('admin/removerVinculo', async ({ idLojista, idEstabelecimento }, { rejectWithValue }) => {
  try {
    await servicoAdmin.removerVinculo(idLojista, idEstabelecimento);
    return { idLojista, idEstabelecimento };
  } catch (erro: unknown) {
    const mensagem = erro instanceof Error ? erro.message : 'Erro ao remover vínculo';
    return rejectWithValue(mensagem);
  }
});

// ============================================================
// Async Thunks — Estabelecimentos
// ============================================================

/**
 * Lista todos os estabelecimentos disponíveis.
 */
export const listarEstabelecimentos = createAsyncThunk<
  EstabelecimentoAdmin[],
  void,
  { rejectValue: string }
>('admin/listarEstabelecimentos', async (_, { rejectWithValue }) => {
  try {
    return await servicoAdmin.listarEstabelecimentos();
  } catch (erro: unknown) {
    const mensagem = erro instanceof Error ? erro.message : 'Erro ao listar estabelecimentos';
    return rejectWithValue(mensagem);
  }
});

// ============================================================
// Estado Inicial
// ============================================================

const estadoInicial: EstadoAdmin = {
  lojistas: [],
  estabelecimentos: [],
  vinculos: [],
  lojistaEditando: null,
  carregando: false,
  erro: null,
  mensagemSucesso: null,
};

// ============================================================
// Slice
// ============================================================

const fatiaAdmin = createSlice({
  name: 'admin',
  initialState: estadoInicial,
  reducers: {
    definirLojistaEditando(state, action: PayloadAction<LojistaAdmin | null>) {
      state.lojistaEditando = action.payload;
    },
    limparErro(state) {
      state.erro = null;
    },
    limparMensagemSucesso(state) {
      state.mensagemSucesso = null;
    },
  },
  extraReducers: (builder) => {
    // ========== Lojistas ==========

    // listarLojistas
    builder
      .addCase(listarLojistas.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(listarLojistas.fulfilled, (state, action) => {
        state.carregando = false;
        state.lojistas = action.payload;
      })
      .addCase(listarLojistas.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao listar lojistas';
      });

    // criarLojista
    builder
      .addCase(criarLojista.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(criarLojista.fulfilled, (state, action) => {
        state.carregando = false;
        state.lojistas.push(action.payload);
        state.mensagemSucesso = 'Lojista criado com sucesso';
      })
      .addCase(criarLojista.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao criar lojista';
      });

    // editarLojista
    builder
      .addCase(editarLojista.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(editarLojista.fulfilled, (state, action) => {
        state.carregando = false;
        const index = state.lojistas.findIndex((l) => l.id === action.payload.id);
        if (index !== -1) {
          state.lojistas[index] = action.payload;
        }
        state.lojistaEditando = null;
        state.mensagemSucesso = 'Lojista editado com sucesso';
      })
      .addCase(editarLojista.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao editar lojista';
      });

    // ativarLojista
    builder
      .addCase(ativarLojista.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(ativarLojista.fulfilled, (state, action) => {
        state.carregando = false;
        const index = state.lojistas.findIndex((l) => l.id === action.payload);
        if (index !== -1) {
          state.lojistas[index].ativo = true;
        }
        state.mensagemSucesso = 'Lojista ativado com sucesso';
      })
      .addCase(ativarLojista.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao ativar lojista';
      });

    // desativarLojista
    builder
      .addCase(desativarLojista.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(desativarLojista.fulfilled, (state, action) => {
        state.carregando = false;
        const index = state.lojistas.findIndex((l) => l.id === action.payload);
        if (index !== -1) {
          state.lojistas[index].ativo = false;
        }
        state.mensagemSucesso = 'Lojista desativado com sucesso';
      })
      .addCase(desativarLojista.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao desativar lojista';
      });

    // resetarSenha
    builder
      .addCase(resetarSenha.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(resetarSenha.fulfilled, (state) => {
        state.carregando = false;
        state.mensagemSucesso = 'Senha resetada com sucesso';
      })
      .addCase(resetarSenha.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao resetar senha';
      });

    // ========== Vínculos ==========

    // listarVinculos
    builder
      .addCase(listarVinculos.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(listarVinculos.fulfilled, (state, action) => {
        state.carregando = false;
        state.vinculos = action.payload;
      })
      .addCase(listarVinculos.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao listar vínculos';
      });

    // criarVinculo
    builder
      .addCase(criarVinculo.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(criarVinculo.fulfilled, (state) => {
        state.carregando = false;
        state.mensagemSucesso = 'Vínculo criado com sucesso';
      })
      .addCase(criarVinculo.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao criar vínculo';
      });

    // removerVinculo
    builder
      .addCase(removerVinculo.pending, (state) => {
        state.carregando = true;
        state.erro = null;
        state.mensagemSucesso = null;
      })
      .addCase(removerVinculo.fulfilled, (state, action) => {
        state.carregando = false;
        state.vinculos = state.vinculos.filter(
          (v) => v.idEstabelecimento !== action.payload.idEstabelecimento
        );
        state.mensagemSucesso = 'Vínculo removido com sucesso';
      })
      .addCase(removerVinculo.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao remover vínculo';
      });

    // ========== Estabelecimentos ==========

    // listarEstabelecimentos
    builder
      .addCase(listarEstabelecimentos.pending, (state) => {
        state.carregando = true;
        state.erro = null;
      })
      .addCase(listarEstabelecimentos.fulfilled, (state, action) => {
        state.carregando = false;
        state.estabelecimentos = action.payload;
      })
      .addCase(listarEstabelecimentos.rejected, (state, action) => {
        state.carregando = false;
        state.erro = action.payload ?? 'Erro ao listar estabelecimentos';
      });
  },
});

export const {
  definirLojistaEditando,
  limparErro: limparErroAdmin,
  limparMensagemSucesso,
} = fatiaAdmin.actions;

export default fatiaAdmin.reducer;
